// src/features/dossiers/components/steps/StepPrestations.jsx
import React, { useState } from 'react';
import {
    Box, Typography, Stack, TextField, Button,
    IconButton, Collapse, MenuItem, Checkbox,
    FormControlLabel, InputAdornment, Divider, alpha,
} from '@mui/material';
import {
    FlightTakeoff, Hotel, HealthAndSafety,
    AddOutlined, DeleteOutlined, ExpandMore, ExpandLess,
    EuroOutlined
} from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { v4 as uuidv4 } from 'uuid';

const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px', fontSize: '0.84rem',
        fontFamily: "'DM Sans', sans-serif",
        '& fieldset': { borderColor: 'rgba(26,26,46,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(201,168,76,0.4)' },
        '&.Mui-focused fieldset': { borderColor: '#c9a84c', borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#c9a84c' },
    '& .MuiSelect-select': { fontFamily: "'DM Sans', sans-serif" },
};

function FieldLabel({ children, required }) {
    return (
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1a1a2e', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.7 }}>
            {children}{required && <Box component="span" sx={{ color: '#c9a84c', ml: 0.3 }}>*</Box>}
        </Typography>
    );
}

const TYPES = [
    { value: 'BILLET', label: 'Billet', icon: FlightTakeoff, color: '#4f8ef7', bg: 'rgba(79,142,247,0.08)' },
    { value: 'RESERVATION_HOTEL', label: 'Hôtel', icon: Hotel, color: '#c9a84c', bg: 'rgba(201,168,76,0.08)' },
    { value: 'ASSURANCE', label: 'Assurance', icon: HealthAndSafety, color: '#6dd9a8', bg: 'rgba(109,217,168,0.08)' },
];

const EMPTY_SEGMENT = () => ({
    _id: uuidv4(), numeroVol: '', compagnieAerienne: '',
    codeAeroportDepart: '', codeAeroportArrivee: '',
    dateHeureDepart: '', dateHeureArrivee: '',
    classeReservation: '', franchiseBagage: '',
});

const EMPTY_PRESTATION = (type = 'BILLET') => ({
    _id: uuidv4(), type,
    prixVente: '', coutAchat: '',
    beneficiairesTempIds: [],
    // BILLET
    pnr: '', numeroBillet: '', segments: [EMPTY_SEGMENT()],
    // HOTEL
    nomHotel: '', ville: '', adresseComplete: '',
    numeroConfirmationFournisseur: '', dateCheckIn: '', dateCheckOut: '',
    typeChambre: '', typePension: '',
    // ASSURANCE
    compagnieAssurance: '', numeroPolice: '', typeCouverture: '',
    zoneGeographique: '', dateDebutCouverture: '', dateFinCouverture: '',
});

// ── Sélecteur de bénéficiaires ────────────────────────────────────────────────
function BeneficiairesSelector({ voyageurs, selectedIds, onChange }) {
    if (!voyageurs?.length) {
        return (
            <Box sx={{ p: 2, borderRadius: '10px', bgcolor: 'rgba(248,113,113,0.06)', border: '1px dashed rgba(248,113,113,0.3)' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#f87171' }}>Ajoutez des voyageurs à l'étape précédente pour les affecter.</Typography>
            </Box>
        );
    }
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {voyageurs.map(v => {
                const checked = selectedIds.includes(v.tempId);
                const name = `${v.prenom} ${v.nom}`.trim() || `Voyageur`;
                const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                return (
                    <Box
                        key={v.tempId}
                        onClick={() => {
                            const updated = checked
                                ? selectedIds.filter(id => id !== v.tempId)
                                : [...selectedIds, v.tempId];
                            onChange(updated);
                        }}
                        sx={{
                            px: 1.5, py: 0.8, borderRadius: '10px', cursor: 'pointer',
                            border: '1px solid', transition: 'all 0.18s',
                            bgcolor: checked ? 'rgba(201,168,76,0.08)' : 'white',
                            borderColor: checked ? 'rgba(201,168,76,0.45)' : 'rgba(26,26,46,0.1)',
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ width: 24, height: 24, borderRadius: '7px', bgcolor: checked ? 'rgba(201,168,76,0.15)' : 'rgba(26,26,46,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: checked ? '#c9a84c' : 'rgba(26,26,46,0.4)' }}>{initials}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: checked ? 700 : 500, color: checked ? '#1a1a2e' : 'rgba(26,26,46,0.55)' }}>{name}</Typography>
                        </Stack>
                    </Box>
                );
            })}
        </Box>
    );
}

// ── Formulaire spécifique BILLET ──────────────────────────────────────────────
function BilletForm({ p, onUpdate }) {
    const addSegment = () => onUpdate({ ...p, segments: [...(p.segments || []), EMPTY_SEGMENT()] });
    const removeSegment = (id) => onUpdate({ ...p, segments: p.segments.filter(s => s._id !== id) });
    const updateSegment = (id, field, value) => onUpdate({ ...p, segments: p.segments.map(s => s._id === id ? { ...s, [field]: value } : s) });

    return (
        <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
                <Box>
                    <FieldLabel>PNR</FieldLabel>
                    <TextField fullWidth size="small" placeholder="ABC123" value={p.pnr} onChange={e => onUpdate({ ...p, pnr: e.target.value })} sx={FIELD_SX} />
                </Box>
                <Box>
                    <FieldLabel>N° Billet</FieldLabel>
                    <TextField fullWidth size="small" placeholder="0570000000000" value={p.numeroBillet} onChange={e => onUpdate({ ...p, numeroBillet: e.target.value })} sx={FIELD_SX} />
                </Box>
            </Box>

            {/* Segments */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(26,26,46,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Segments de vol ({p.segments?.length})
                </Typography>
                <Button onClick={addSegment} size="small" startIcon={<AddCircleIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{ color: '#4f8ef7', fontFamily: "'DM Sans', sans-serif", textTransform: 'none', fontSize: '0.72rem', fontWeight: 600 }}>
                    Ajouter un segment
                </Button>
            </Stack>

            <Stack spacing={1.5}>
                {(p.segments || []).map((seg, si) => (
                    <Box key={seg._id} sx={{ p: 2, borderRadius: '12px', bgcolor: 'rgba(79,142,247,0.04)', border: '1px solid rgba(79,142,247,0.12)', position: 'relative' }}>
                        {p.segments.length > 1 && (
                            <IconButton size="small" onClick={() => removeSegment(seg._id)}
                                sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(26,26,46,0.3)', '&:hover': { color: '#f87171' } }}>
                                <RemoveCircleIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                        <Typography sx={{ fontSize: '0.67rem', fontWeight: 800, color: '#4f8ef7', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1.5 }}>
                            Segment {si + 1}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5, mb: 1.5 }}>
                            <Box>
                                <FieldLabel>N° Vol</FieldLabel>
                                <TextField fullWidth size="small" placeholder="AF625" value={seg.numeroVol} onChange={e => updateSegment(seg._id, 'numeroVol', e.target.value)} sx={FIELD_SX} />
                            </Box>
                            <Box sx={{ gridColumn: 'span 2' }}>
                                <FieldLabel>Compagnie aérienne</FieldLabel>
                                <TextField fullWidth size="small" placeholder="Air France" value={seg.compagnieAerienne} onChange={e => updateSegment(seg._id, 'compagnieAerienne', e.target.value)} sx={FIELD_SX} />
                            </Box>
                            <Box>
                                <FieldLabel>Franchise bagage</FieldLabel>
                                <TextField fullWidth size="small" placeholder="2x23kg" value={seg.franchiseBagage} onChange={e => updateSegment(seg._id, 'franchiseBagage', e.target.value)} sx={FIELD_SX} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5 }}>
                            <Box>
                                <FieldLabel>Départ (IATA)</FieldLabel>
                                <TextField fullWidth size="small" placeholder="CDG" inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }} value={seg.codeAeroportDepart} onChange={e => updateSegment(seg._id, 'codeAeroportDepart', e.target.value.toUpperCase())} sx={FIELD_SX} />
                            </Box>
                            <Box>
                                <FieldLabel>Arrivée (IATA)</FieldLabel>
                                <TextField fullWidth size="small" placeholder="ABJ" inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }} value={seg.codeAeroportArrivee} onChange={e => updateSegment(seg._id, 'codeAeroportArrivee', e.target.value.toUpperCase())} sx={FIELD_SX} />
                            </Box>
                            <Box>
                                <FieldLabel>Date/heure départ</FieldLabel>
                                <TextField fullWidth size="small" type="datetime-local" value={seg.dateHeureDepart} onChange={e => updateSegment(seg._id, 'dateHeureDepart', e.target.value)} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
                            </Box>
                            <Box>
                                <FieldLabel>Date/heure arrivée</FieldLabel>
                                <TextField fullWidth size="small" type="datetime-local" value={seg.dateHeureArrivee} onChange={e => updateSegment(seg._id, 'dateHeureArrivee', e.target.value)} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
                            </Box>
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                            <FieldLabel>Classe de réservation</FieldLabel>
                            <TextField fullWidth size="small" placeholder="Business / Economy..." value={seg.classeReservation} onChange={e => updateSegment(seg._id, 'classeReservation', e.target.value)} sx={FIELD_SX} />
                        </Box>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

// ── Formulaire spécifique HOTEL ───────────────────────────────────────────────
function HotelForm({ p, onUpdate }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box sx={{ gridColumn: 'span 2' }}>
                <FieldLabel required>Nom de l'hôtel</FieldLabel>
                <TextField fullWidth size="small" placeholder="Royal Mansour" value={p.nomHotel} onChange={e => onUpdate({ ...p, nomHotel: e.target.value })} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel required>Ville</FieldLabel>
                <TextField fullWidth size="small" placeholder="Marrakech" value={p.ville} onChange={e => onUpdate({ ...p, ville: e.target.value })} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel>N° Confirmation fournisseur</FieldLabel>
                <TextField fullWidth size="small" placeholder="RM-2026-0001" value={p.numeroConfirmationFournisseur} onChange={e => onUpdate({ ...p, numeroConfirmationFournisseur: e.target.value })} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel required>Check-in</FieldLabel>
                <TextField fullWidth size="small" type="date" value={p.dateCheckIn} onChange={e => onUpdate({ ...p, dateCheckIn: e.target.value })} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel required>Check-out</FieldLabel>
                <TextField fullWidth size="small" type="date" value={p.dateCheckOut} onChange={e => onUpdate({ ...p, dateCheckOut: e.target.value })} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel>Type de chambre</FieldLabel>
                <TextField fullWidth size="small" placeholder="Suite Prestige" value={p.typeChambre} onChange={e => onUpdate({ ...p, typeChambre: e.target.value })} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel>Type de pension</FieldLabel>
                <TextField fullWidth size="small" placeholder="Petit-déjeuner inclus" value={p.typePension} onChange={e => onUpdate({ ...p, typePension: e.target.value })} sx={FIELD_SX} />
            </Box>
            <Box sx={{ gridColumn: 'span 2' }}>
                <FieldLabel>Adresse complète</FieldLabel>
                <TextField fullWidth size="small" placeholder="Rue Abou Abbas El Sebti, Marrakech" value={p.adresseComplete} onChange={e => onUpdate({ ...p, adresseComplete: e.target.value })} sx={FIELD_SX} />
            </Box>
        </Box>
    );
}

// ── Formulaire spécifique ASSURANCE ──────────────────────────────────────────
function AssuranceForm({ p, onUpdate }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
                <FieldLabel required>Compagnie d'assurance</FieldLabel>
                <TextField fullWidth size="small" placeholder="AXA Assistance" value={p.compagnieAssurance} onChange={e => onUpdate({ ...p, compagnieAssurance: e.target.value })} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel>N° Police</FieldLabel>
                <TextField fullWidth size="small" placeholder="AXA-2026-0001" value={p.numeroPolice} onChange={e => onUpdate({ ...p, numeroPolice: e.target.value })} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel>Type de couverture</FieldLabel>
                <TextField fullWidth size="small" placeholder="Tous risques voyage" value={p.typeCouverture} onChange={e => onUpdate({ ...p, typeCouverture: e.target.value })} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel>Zone géographique</FieldLabel>
                <TextField fullWidth size="small" placeholder="Monde entier" value={p.zoneGeographique} onChange={e => onUpdate({ ...p, zoneGeographique: e.target.value })} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel>Début de couverture</FieldLabel>
                <TextField fullWidth size="small" type="date" value={p.dateDebutCouverture} onChange={e => onUpdate({ ...p, dateDebutCouverture: e.target.value })} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
            </Box>
            <Box>
                <FieldLabel>Fin de couverture</FieldLabel>
                <TextField fullWidth size="small" type="date" value={p.dateFinCouverture} onChange={e => onUpdate({ ...p, dateFinCouverture: e.target.value })} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
            </Box>
        </Box>
    );
}

// ── Carte prestation ──────────────────────────────────────────────────────────
function PrestationCard({ prestation, voyageurs, onUpdate, onRemove, index }) {
    const [open, setOpen] = useState(true);
    const meta = TYPES.find(t => t.value === prestation.type) || TYPES[0];
    const Icon = meta.icon;
    const marge = prestation.prixVente && prestation.coutAchat
        ? Number(prestation.prixVente) - Number(prestation.coutAchat) : null;

    return (
        <Box sx={{ borderRadius: '18px', border: '1px solid', borderColor: open ? meta.color + '40' : 'rgba(26,26,46,0.08)', overflow: 'hidden', transition: 'border-color 0.2s' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1.5}
                onClick={() => setOpen(!open)}
                sx={{ p: 2.5, cursor: 'pointer', bgcolor: open ? meta.bg : 'white', '&:hover': { bgcolor: meta.bg }, userSelect: 'none' }}
            >
                <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: `${meta.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon sx={{ fontSize: 18, color: meta.color }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e' }}>{meta.label} {index + 1}</Typography>
                        {prestation.beneficiairesTempIds?.length > 0 && (
                            <Box sx={{ px: 1, py: 0.2, borderRadius: '6px', bgcolor: `${meta.color}18` }}>
                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: meta.color }}>{prestation.beneficiairesTempIds.length} bénéf.</Typography>
                            </Box>
                        )}
                    </Stack>
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(26,26,46,0.45)' }}>
                        {prestation.prixVente ? `${Number(prestation.prixVente).toLocaleString()} € HV` : 'Prix non défini'}
                        {marge != null && ` · Marge : ${marge >= 0 ? '+' : ''}${marge.toLocaleString()} €`}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={e => { e.stopPropagation(); onRemove(); }}
                        sx={{ color: 'rgba(26,26,46,0.25)', '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,0.08)' } }}>
                        <DeleteOutlined sx={{ fontSize: 16 }} />
                    </IconButton>
                    {open ? <ExpandLess sx={{ fontSize: 18, color: 'rgba(26,26,46,0.4)' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'rgba(26,26,46,0.4)' }} />}
                </Stack>
            </Stack>

            <Collapse in={open}>
                <Box sx={{ p: 2.5, bgcolor: 'white', borderTop: `1px solid ${meta.color}20` }}>
                    {/* Prix */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
                        <Box>
                            <FieldLabel required>Prix de vente (€)</FieldLabel>
                            <TextField fullWidth size="small" type="number" placeholder="0.00" value={prestation.prixVente}
                                onChange={e => onUpdate({ ...prestation, prixVente: e.target.value })}
                                InputProps={{ startAdornment: <InputAdornment position="start"><EuroOutlined sx={{ fontSize: 15, color: 'rgba(26,26,46,0.35)' }} /></InputAdornment> }}
                                sx={FIELD_SX} />
                        </Box>
                        <Box>
                            <FieldLabel>Coût d'achat (€)</FieldLabel>
                            <TextField fullWidth size="small" type="number" placeholder="0.00" value={prestation.coutAchat}
                                onChange={e => onUpdate({ ...prestation, coutAchat: e.target.value })}
                                InputProps={{ startAdornment: <InputAdornment position="start"><EuroOutlined sx={{ fontSize: 15, color: 'rgba(26,26,46,0.35)' }} /></InputAdornment> }}
                                sx={FIELD_SX} />
                        </Box>
                    </Box>

                    {/* Bénéficiaires */}
                    <Box sx={{ mb: 2.5 }}>
                        <FieldLabel>Bénéficiaires</FieldLabel>
                        <BeneficiairesSelector
                            voyageurs={voyageurs}
                            selectedIds={prestation.beneficiairesTempIds || []}
                            onChange={ids => onUpdate({ ...prestation, beneficiairesTempIds: ids })}
                        />
                    </Box>

                    <Divider sx={{ mb: 2.5, borderColor: 'rgba(26,26,46,0.07)' }} />

                    {/* Champs spécifiques */}
                    {prestation.type === 'BILLET' && <BilletForm p={prestation} onUpdate={onUpdate} />}
                    {prestation.type === 'RESERVATION_HOTEL' && <HotelForm p={prestation} onUpdate={onUpdate} />}
                    {prestation.type === 'ASSURANCE' && <AssuranceForm p={prestation} onUpdate={onUpdate} />}
                </Box>
            </Collapse>
        </Box>
    );
}

// ── Composant principal ───────────────────────────────────────────────────────
export function StepPrestations({ data, onChange }) {
    const prestations = data.prestations || [];
    const voyageurs = data.voyageurs || [];

    const addPrestation = (type) => {
        const p = EMPTY_PRESTATION(type);
        // ✅ Fix : lecture fraîche de data.voyageurs (pas la variable closurée)
        // et spread dans un nouvel objet pour éviter toute mutation silencieuse
        const allTempIds = (data.voyageurs || []).map(v => v.tempId);
        const newPrestation = { ...p, beneficiairesTempIds: allTempIds };
        onChange({ prestations: [...(data.prestations || []), newPrestation] });
    };

    const updatePrestation = (id, updated) => {
        onChange({ prestations: prestations.map(p => p._id === id ? updated : p) });
    };

    const removePrestation = (id) => {
        onChange({ prestations: prestations.filter(p => p._id !== id) });
    };

    const total = prestations.reduce((sum, p) => sum + (Number(p.prixVente) || 0), 0);

    return (
        <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Typography sx={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.35rem', fontWeight: 700, color: '#1a1a2e' }}>
                    Prestations
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", px: 1.2, py: 0.3, borderRadius: '8px', bgcolor: '#1a1a2e', minWidth: "30px" }}>
                    <Typography sx={{ color: '#c9a84c', fontSize: '0.8rem', fontWeight: 800 }}>{prestations.length}</Typography>
                </Box>
                {total > 0 && (
                    <>
                        <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'rgba(26,26,46,0.07)' }} />
                        <Typography sx={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e' }}>
                            Total : {total.toLocaleString()} €
                        </Typography>
                    </>
                )}
                {total === 0 && <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'rgba(26,26,46,0.07)' }} />}
            </Stack>

            {/* Liste prestations */}
            <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                {prestations.map((p, i) => (
                    <PrestationCard
                        key={p._id} prestation={p} index={i}
                        voyageurs={voyageurs}
                        onUpdate={updated => updatePrestation(p._id, updated)}
                        onRemove={() => removePrestation(p._id)}
                    />
                ))}
            </Stack>

            {/* Boutons ajout par type */}
            <Box sx={{ p: 2.5, borderRadius: '16px', bgcolor: 'rgba(26,26,46,0.02)', border: '1px dashed rgba(26,26,46,0.12)' }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(26,26,46,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.5 }}>
                    Ajouter une prestation
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1.5 }}>
                    {TYPES.map(t => {
                        const Icon = t.icon;
                        return (
                            <Button key={t.value} onClick={() => addPrestation(t.value)}
                                startIcon={<Icon sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                    px: 2.5, py: 1.2, borderRadius: '12px',
                                    bgcolor: t.bg, color: t.color,
                                    border: `1px solid ${t.color}30`,
                                    fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.82rem',
                                    textTransform: 'none',
                                    transition: 'all 0.18s',
                                    '&:hover': { bgcolor: `${t.color}18`, borderColor: `${t.color}60`, transform: 'translateY(-1px)', boxShadow: `0 4px 12px ${t.color}20` },
                                }}
                            >
                                {t.label}
                            </Button>
                        );
                    })}
                </Stack>
            </Box>
        </Box>
    );
}